import { Box, Card, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SizedImage from '../../common/components/SizedImage';
import FileUtils from '../../common/utils/file.utils';
import CompanyLogo from '../../common/components/CompanyLogo';
import CompanyInfoChips from '../CompanyInfoChips';
import { currentContextAtom } from '../../state';
import SearchUtils from '../../common/utils/search.utils';
import { formatCompanyAddress } from '../../common/utils/common.utils';
import ContactBox from './ContactBox';
import { CompanyInfo } from './CompanyInfoCard';
import { ContractPreview } from '../../hooks/useContracts';

export interface UnsignedBuyerInfoCardProps {
  company: CompanyInfo;
  contract?: ContractPreview;
  compact?: boolean;
}

export default function UnsignedBuyerInfoCard({ company, contract, compact }: UnsignedBuyerInfoCardProps): React.JSX.Element {
  const { t } = useTranslation(['common']);
  const context = useAtomValue(currentContextAtom);

  const canDeliver = useMemo(() => {
    return SearchUtils.canItDeliverAnArea(context.deliveryZones, company.addresses[0].administrativeAreaLevel2!);
  }, [context, company]);

  const companyInfoAndAddress = useMemo(() => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          alignItems: {
            sm: 'center',
          },
          gap: {
            xs: 0.5,
          },
          marginTop: 1.5,
        }}
      >
        <CompanyInfoChips company={company} contract={contract} canDeliver={canDeliver} />
        <Typography variant="bodyMedium" color="secondary">
          {formatCompanyAddress(company.addresses[0])}
        </Typography>
      </Box>
    );
  }, [contract, company, canDeliver]);

  if (compact) {
    return (
      <Box sx={{ mb: 2 }}>
        {companyInfoAndAddress}
        <ContactBox contactCompanyId={company.id} contract={contract} />
      </Box>
    );
  }

  return (
    <Card
      sx={{
        display: 'flex',
        p: { lg: 1.5 },
        backgroundColor: 'surfaceContainer.main',
        flexDirection: 'row',
        justifyContent: 'normal',
        my: 2,
        borderRadius: 25,
        alignItems: 'center',
      }}
    >
      <SizedImage
        src={FileUtils.BuildCompanyImageUrl({
          photo: company.photos ? company.photos[0]?.photo : undefined,
          type: company.business_profile,
          size: { height: 80, width: 80 },
        })}
        alt={t('common:company')}
        height={80}
        width={80}
        borderRadius={25}
        sx={{
          mr: 1.5,
          overflow: 'visible',
          '& img': {
            borderRadius: 25,
          },
        }}
        overlay={company.brand && (
          <CompanyLogo
            brand={company.brand}
            size="medium"
            variant="circle"
            sx={{
              position: 'absolute',
              bottom: {
                lg: 0,
              },
              right: {
                lg: 0,
              },
              width: {
                lg: 40,
              },
            }}
          />
        )}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          m: 0,
        }}
      >
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="titleLarge"
          >
            {company.tradeName}
          </Typography>
          {companyInfoAndAddress}
        </Box>
        <ContactBox contactCompanyId={company.id} contract={contract} />
      </Box>
    </Card>
  );
}
